/* eslint-disable max-lines */
import styled, { css, keyframes } from 'styled-components';

import { ds } from '@/config/colors';
import { LocaleNavLink } from '@/utils/locale';

import { media } from '../../../utils/mediaQueries';

interface SidebarProps {
    $collapsed?: boolean;
    $isMobileMenuOpen?: boolean;
    $isCollapsed?: boolean;
    $isPinned?: boolean;
}

/* ==========================================================================
   Mikro-animacje ikon sidebara (SidebarIcons) — odpalane po najechaniu na cały
   wiersz menu. Element-sygnatura każdej ikony jest oznaczony klasą `sb-*`;
   poniższe keyframes + mixin `iconHoverFx` wstrzykujemy w `MenuLink` oraz
   `MenuGroupButton`. Wszystko gaśnie przy `prefers-reduced-motion`.
   ========================================================================== */

const sbDraw = keyframes`
    from { stroke-dashoffset: 52; }
    to { stroke-dashoffset: 0; }
`;
const sbSpin = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
`;
const sbSwing = keyframes`
    0%, 100% { transform: rotate(0deg); }
    25% { transform: rotate(-11deg); }
    60% { transform: rotate(7deg); }
    82% { transform: rotate(-3deg); }
`;
const sbPop = keyframes`
    0%, 100% { transform: scale(1); }
    45% { transform: scale(1.32); }
`;
const sbFloat = keyframes`
    0%, 100% { transform: translateY(0); }
    45% { transform: translateY(-2.5px); }
`;
const sbWave = keyframes`
    0% { opacity: 0; transform: scale(0.55); }
    45% { opacity: 1; }
    100% { opacity: 0; transform: scale(1.2); }
`;
const sbBlink = keyframes`
    0%, 38%, 100% { transform: scaleY(1); }
    52% { transform: scaleY(0.12); }
`;
const sbBounce = keyframes`
    0%, 100% { transform: translateY(0); }
    40% { transform: translateY(-3px); }
`;
const sbBars = keyframes`
    0% { transform: scaleY(0.5); }
    60% { transform: scaleY(1.08); }
    100% { transform: scaleY(1); }
`;
// Wskazówka „omiata" tarczę (gauge Dashboardu) i wraca do pozycji spoczynkowej.
const sbSweep = keyframes`
    0% { transform: rotate(-52deg); }
    70% { transform: rotate(7deg); }
    100% { transform: rotate(0deg); }
`;

// Punkt obrotu/skalowania liczony względem własnego bounding-boxa elementu SVG.
const iconHoverFx = css`
    .sb-spin, .sb-swing, .sb-sweep, .sb-pop, .sb-float, .sb-wave, .sb-blink, .sb-bounce {
        transform-box: fill-box;
        transform-origin: center;
    }
    .sb-bars {
        transform-box: fill-box;
        transform-origin: bottom center;
    }
    .sb-draw {
        stroke-dasharray: 52;
        stroke-dashoffset: 0;
    }

    &:hover {
        .sb-draw { animation: ${sbDraw} 0.85s ease; }
        .sb-spin { animation: ${sbSpin} 0.9s ease; }
        .sb-swing { animation: ${sbSwing} 0.8s ease; }
        .sb-sweep { animation: ${sbSweep} 0.9s ease; }
        .sb-pop { animation: ${sbPop} 0.6s ease; }
        .sb-pop-2 { animation-delay: 0.12s; }
        .sb-pop-3 { animation-delay: 0.24s; }
        .sb-pop-4 { animation-delay: 0.36s; }
        .sb-pop-5 { animation-delay: 0.48s; }
        .sb-float { animation: ${sbFloat} 0.9s ease; }
        .sb-blink { animation: ${sbBlink} 0.8s ease; }
        .sb-wave { animation: ${sbWave} 0.9s ease; }
        .sb-wave-2 { animation-delay: 0.14s; }
        .sb-wave-3 { animation-delay: 0.28s; }
        .sb-bounce { animation: ${sbBounce} 0.7s ease; }
        .sb-bounce-2 { animation-delay: 0.1s; }
        .sb-bounce-3 { animation-delay: 0.2s; }
        .sb-bars { animation: ${sbBars} 0.8s ease; }
        .sb-bars-2 { animation-delay: 0.12s; }
        .sb-bars-3 { animation-delay: 0.24s; }
    }

    @media (prefers-reduced-motion: reduce) {
        &:hover [class*='sb-'] { animation: none; }
    }
`;

/* ==========================================================================
   Paleta sidebara — jasny motyw (wzorowany na załączonym projekcie): białe tło,
   ciemnoszary tekst, szare ikony, aktywny element = ciemna wypełniona pigułka.
   W trybie ciemnym: ciemna powierzchnia + jasny tekst.
   ========================================================================== */
const sbBg = (mode: string) => (mode === 'dark' ? '#1a1f26' : '#ffffff');
const sbText = (mode: string) => (mode === 'dark' ? '#f3f4f6' : '#1f2937');
const sbIcon = (mode: string) => (mode === 'dark' ? '#e5e7eb' : '#374151');
const sbTextHover = (mode: string) => (mode === 'dark' ? '#ffffff' : '#111827');
const sbHoverBg = (mode: string) => (mode === 'dark' ? 'rgba(255,255,255,0.05)' : '#f3f4f6');
const sbActiveBg = (mode: string) => (mode === 'dark'
  ? 'rgba(255,255,255,0.10)'
  : '#ededee');
const sbDivider = (mode: string) => (mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#dcdfe4');

export const SidebarWrapper = styled.aside<SidebarProps>`
    position: relative;
    display: flex;
    flex-direction: column;
    background: ${({ theme }) => sbBg(theme.mode)};

    /* Desktop: komórka siatki 72 px pod paskiem górnym, z linią po prawej.
       Panel (SidebarPanel) rozwija się z niej nad treść, nie przesuwając układu. */
    ${media.desktop} {
        grid-column: 2;
        grid-row: 2;
        width: ${({ $isPinned }) => ($isPinned ? '272px' : '72px')};
        min-height: 0;
        z-index: 20; /* poniżej paska górnego (z-46) i modali HeroUI (z-50) */

        /* Rozwinięty panel: nad etykietami pól HeroUI w treści, nadal poniżej modali. */
        &:has([data-open='true']) {
            z-index: 45;
        }
        overflow: visible;

        /* Niewidoczna strefa najazdu na pustym marginesie po lewej: od krawędzi ekranu do paska. */
        &::before {
            content: '';
            position: absolute;
            top: 0;
            bottom: 0;
            right: 100%;
            width: var(--hs-gutter, 32px);
        }
    }

    /* Telefon i tablet: szuflada wysuwana z lewej, nad paskiem górnym i treścią. */
    ${media.tabletDown} {
        position: fixed;
        left: 0;
        top: 0;
        bottom: 0;
        width: min(320px, 86vw);
        z-index: 1010;
        box-shadow: 8px 0 24px rgba(15, 23, 42, 0.12);
        will-change: transform;
        transform: translateX(${({ $isMobileMenuOpen }) => ($isMobileMenuOpen ? '0' : '-100%')});
        visibility: ${({ $isMobileMenuOpen }) => ($isMobileMenuOpen ? 'visible' : 'hidden')};
        transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1), visibility 0.25s;
    }
`;

export const MobileOverlay = styled.div<{ $isOpen?: boolean }>`
    display: none;
    position: fixed;
    inset: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 1005;
    opacity: ${({ $isOpen }) => ($isOpen ? 1 : 0)};
    visibility: ${({ $isOpen }) => ($isOpen ? 'visible' : 'hidden')};
    transition: opacity 0.3s ease, visibility 0.3s ease;

    ${media.tabletDown} {
        display: block;
    }
`;

export const MenuLinkWrapper = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    width: 100%;
    min-width: 0;
`;

export const MenuLink = styled(LocaleNavLink)<{
    $isSubItem?: boolean;
    $disabled?: boolean;
    $isCollapsed?: boolean;
}>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: ${({ $isCollapsed }) => ($isCollapsed ? 'center' : 'flex-start')};
    width: 100%;
    font-size: 13px;
    line-height: 1.4;
    color: ${({ theme }) => sbText(theme.mode)};
    padding: ${({ $isCollapsed }) => ($isCollapsed ? '6px' : '3px 9px')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: 10px;
    margin: 0;
    font-weight: 500;
    transition: color 0.15s ease, background 0.15s ease;

    svg {
        color: ${({ theme }) => sbIcon(theme.mode)};
    }

    ${media.mobile} {
        padding: ${({ $isCollapsed }) => ($isCollapsed ? '6px' : '4px 9px')};
        font-size: 13px;
    }

    /* Disabled/Soon state */
    ${({ $disabled }) => $disabled && `
        pointer-events: none;
        opacity: 0.5;
        cursor: not-allowed;
    `}

    &:hover {
        ${({ $disabled, theme }) => !$disabled && `
            color: ${sbTextHover(theme.mode)};
            background: ${sbHoverBg(theme.mode)};

            svg {
                color: ${sbTextHover(theme.mode)};
            }
        `}
    }

    ${iconHoverFx}

    /* Active item — ciemna wypełniona pigułka + biały tekst/ikony */
    &.active {
        ${({ $disabled, theme }) => !$disabled && `
            color: ${sbTextHover(theme.mode)};
            font-weight: 600;
            background: ${sbActiveBg(theme.mode)};

            svg {
                color: ${sbTextHover(theme.mode)};
            }

            &:hover {
                color: ${sbTextHover(theme.mode)};
                background: ${sbActiveBg(theme.mode)};

                svg { color: ${sbTextHover(theme.mode)}; }
            }
        `}
    }

    > * {
        position: relative;
        z-index: 1;
    }
`;

/* Wariant button dla grup z flyoutem — taki sam wygląd jak MenuLink. */
export const MenuGroupButton = styled.button<{
    $disabled?: boolean;
    $isCollapsed?: boolean;
    $isActive?: boolean;
}>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: ${({ $isCollapsed }) => ($isCollapsed ? 'center' : 'flex-start')};
    width: 100%;
    font-size: 13px;
    line-height: 1.4;
    color: ${({ theme }) => sbText(theme.mode)};
    padding: ${({ $isCollapsed }) => ($isCollapsed ? '6px' : '3px 9px')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: 10px;
    margin: 0;
    font-weight: 500;
    background: transparent;
    border: none;
    cursor: pointer;
    font-family: inherit;
    text-align: left;
    transition: color 0.15s ease, background 0.15s ease;

    svg {
        color: ${({ theme }) => sbIcon(theme.mode)};
    }

    ${media.mobile} {
        padding: ${({ $isCollapsed }) => ($isCollapsed ? '6px' : '4px 9px')};
        font-size: 13px;
    }

    ${({ $disabled }) => $disabled && `
        pointer-events: none;
        opacity: 0.5;
        cursor: not-allowed;
    `}

    &:hover {
        ${({ $disabled, theme }) => !$disabled && `
            color: ${sbTextHover(theme.mode)};
            background: ${sbHoverBg(theme.mode)};

            svg { color: ${sbTextHover(theme.mode)}; }
        `}
    }

    ${iconHoverFx}

    /* Aktywny flyout / grupa z aktywnym dzieckiem — ciemna pigułka */
    ${({ $isActive, theme }) => $isActive && `
        color: ${sbTextHover(theme.mode)};
        font-weight: 600;
        background: ${sbActiveBg(theme.mode)};

        svg { color: ${sbTextHover(theme.mode)}; }

        &:hover {
            color: ${sbTextHover(theme.mode)};
            background: ${sbActiveBg(theme.mode)};
            svg { color: ${sbTextHover(theme.mode)}; }
        }
    `}

    > * {
        position: relative;
        z-index: 1;
    }
`;

export const MenuLinkLogout = styled.button<{ $isSubItem?: boolean; $danger?: boolean; $isCollapsed?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: ${({ $isCollapsed }) => ($isCollapsed ? 'center' : 'flex-start')};
    width: 100%;
    font-size: 12.5px;
    line-height: 1.4;
    color: ${({ $danger, theme }) => ($danger ? theme.colors.danger[500] : sbText(theme.mode))};
    padding: ${({ $isCollapsed }) => ($isCollapsed ? '7px' : '6px 9px')};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    border-radius: 8px;
    margin: 1px 0;
    font-weight: 500;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: color 0.2s ease, background 0.2s ease;

    ${media.mobile} {
        padding: ${({ $isCollapsed }) => ($isCollapsed ? '7px' : '7px 9px')};
        font-size: 12.5px;
    }

    &:hover {
        color: ${({ $danger, theme }) => {
    if ($danger) return theme.colors.danger[500];
    return sbTextHover(theme.mode);
  }};
        background: ${({ $danger, theme }) => ($danger ? 'transparent' : sbHoverBg(theme.mode))};

        svg {
            color: ${({ $danger, theme }) => {
    if ($danger) return theme.colors.danger[500];
    return sbTextHover(theme.mode);
  }};
        }
    }

    > * {
        position: relative;
        z-index: 1;
    }
`;

export const MenuLinkText = styled.span`
    flex: 1;
    min-width: 0;
    overflow: hidden;
    text-overflow: ellipsis;
    font-size: 13px;
    font-weight: inherit;
`;

export const MenuLinkIcoWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 18px;
    height: 18px;
    flex-shrink: 0;

    svg {
        width: 18px;
        height: 18px;
    }
`;

export const SidebarLogoWrapper = styled.div`
    position: relative;
    box-sizing: border-box;
    padding: 0 10px 0 16px;
    height: 68px;
    display: flex;
    align-items: center;
    justify-content: space-between;
    background: transparent;

    /* W trybie ciemnym logo wybielamy; w jasnym pokazujemy naturalny kolor. */
    a > svg,
    & > a > svg {
        height: 28px;
        width: auto;
        filter: ${({ theme }) => (theme.mode === 'dark' ? 'brightness(0) invert(1)' : 'none')};
    }

    ${media.tablet} {
        height: 60px;
    }

    ${media.mobile} {
        padding: 0 8px 0 14px;
        height: 52px;
    }
`;

export const LogoContainer = styled.div<{ $isCollapsed?: boolean }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
    cursor: pointer;
    padding: 6px;
    border-radius: 8px;

    &:hover {
        cursor: ${({ $isCollapsed }) => ($isCollapsed ? 'e-resize' : 'pointer')};
    }

    ${media.mobile} {
        padding: 5px;
    }
`;

export const CollapseButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    width: 34px;
    height: 34px;
    padding: 0;
    background: transparent;
    border: none;
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
    border-radius: 10px;
    color: ${({ theme }) => sbIcon(theme.mode)};

    &:hover {
        background: ${({ theme }) => sbHoverBg(theme.mode)};
        color: ${({ theme }) => sbTextHover(theme.mode)};
        cursor: w-resize;
    }

    svg {
        width: 18px;
        height: 18px;
        color: inherit;
    }

    ${media.tabletDown} {
        display: none;
    }
`;

export const LogoHoverOverlay = styled.div`
    position: absolute;
    inset: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background: ${({ theme }) => sbHoverBg(theme.mode)};
    border-radius: 8px;
    z-index: 2;

    svg {
        color: ${({ theme }) => sbTextHover(theme.mode)};
        width: 18px;
        height: 18px;
    }
`;

export const MobileCloseButton = styled.button`
    background: transparent;
    border: none;
    cursor: pointer;
    width: 40px;
    height: 40px;
    padding: 0;
    display: none;
    align-items: center;
    justify-content: center;
    border-radius: 9999px;
    transition: background 0.2s ease;

    svg {
        color: ${({ theme }) => sbIcon(theme.mode)};
    }

    &:hover {
        background: ${({ theme }) => sbHoverBg(theme.mode)};

        svg { color: ${({ theme }) => sbTextHover(theme.mode)}; }
    }

    ${media.tabletDown} {
        display: flex;
    }
`;

export const MenuItemsWrapper = styled.div`
    flex: 1;
    display: flex;
    flex-direction: column;
    /* Wiersze stały dotąd stykami i przy dłuższej liście zlewały się w jeden blok.
       Jeden piksel wystarcza: resztę oddechu dają większe paddingi wierszy. */
    gap: 0;
    padding: 4px 8px;
    overflow-y: auto;

    ${media.mobile} {
        padding: 5px 8px;
    }

    &::-webkit-scrollbar {
        width: 6px;
    }

    &::-webkit-scrollbar-track {
        background: transparent;
    }

    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(15,23,42,0.14)')};
        border-radius: 6px;
    }

    &::-webkit-scrollbar-thumb:hover {
        background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.28)' : 'rgba(15,23,42,0.24)')};
    }
`;

export const BottomLinksWrapper = styled.div`
    border-top: 1px solid ${({ theme }) => sbDivider(theme.mode)};
    padding: 9px 10px;
    background: transparent;
    margin-top: auto;

    ${media.mobile} {
        padding: 8px;
    }
`;

export const SectionLabel = styled.div`
    font-size: 10px;
    font-weight: 600;
    text-transform: uppercase;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#9ca3af' : '#6b7280')};
    /* Więcej powietrza NAD nagłówkiem niż pod nim: etykieta ma się kleić do swojej
       sekcji, a nie wisieć w połowie drogi między dwiema. */
    padding: 8px 9px 2px;
    margin-bottom: 0;
    letter-spacing: 0.07em;
    position: relative;

    ${media.mobile} {
        padding: 8px 8px 2px;
        font-size: 10px;
    }
`;

// Zwinięty sidebar nie ma miejsca na etykietę sekcji — zostaje sama kreska,
// żeby podział na sekcje nie znikał razem z tekstem.
export const SectionRule = styled.div`
    height: 1px;
    margin: 7px 8px 5px;
    background: ${({ theme }) => sbDivider(theme.mode)};
`;

export const QuickAccessWrapper = styled.div<{ $isCollapsed?: boolean }>`
    display: flex;
    flex-direction: ${({ $isCollapsed }) => ($isCollapsed ? 'column' : 'row')};
    align-items: center;
    justify-content: center;
    gap: 10px;
    padding: 9px 12px;
    margin-bottom: 6px;
    border-top: 1px solid ${({ theme }) => sbDivider(theme.mode)};
    border-bottom: 1px solid ${({ theme }) => sbDivider(theme.mode)};

    ${media.mobile} {
        padding: 8px 12px;
        gap: 6px;
    }
`;

export const QuickAccessButton = styled(LocaleNavLink)<{ $isCollapsed?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 50%;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#f3f4f6')};
    border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#dcdfe4')};
    color: ${({ theme }) => sbIcon(theme.mode)};
    transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
    cursor: pointer;

    svg {
        width: 13px;
        height: 13px;
    }

    &:hover {
        background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.15)' : '#e5e7eb')};
        border-color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.3)' : '#d1d5db')};
        color: ${({ theme }) => sbTextHover(theme.mode)};
    }

    &.active {
        background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.2)' : '#111827')};
        border-color: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.3)' : '#111827')};
        color: #ffffff;
    }

    ${media.mobile} {
        width: 32px;
        height: 32px;

        svg {
            width: 14px;
            height: 14px;
        }
    }
`;

export const QuickAccessButtonWrapper = styled.div`
    position: relative;
    display: inline-flex;
`;

export const QuickAccessBadge = styled.span`
    position: absolute;
    top: -6px;
    right: -6px;
    min-width: 18px;
    height: 18px;
    padding: 0 5px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
    font-weight: 400;
    color: white;
    background: #dc2626;
    border-radius: 9999px;
    border: 2px solid ${({ theme }) => sbBg(theme.mode)};
    line-height: 1;
`;

export const MenuIconWrapper = styled.div`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: center;
`;

export const MenuItemBadge = styled.span<{ $expanded?: boolean }>`
    ${({ $expanded, theme }) => ($expanded ? `
        margin-left: auto;
        padding: 1px 7px;
        background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#f3f4f6'};
        border-radius: 10px;
        color: ${theme.mode === 'dark' ? '#f3f4f6' : '#374151'};
        font-size: 10px;
        font-weight: 400;
        line-height: 1;
        min-width: 18px;
        text-align: center;
        display: inline-flex;
        align-items: center;
        justify-content: center;
    ` : `
        position: absolute;
        top: -4px;
        right: -4px;
        padding: 1px 4px;
        background: ${theme.mode === 'dark' ? '#374151' : '#6b7280'};
        border-radius: 10px;
        color: #ffffff;
        font-size: 9px;
        font-weight: 400;
        line-height: 1;
        min-width: 16px;
        text-align: center;
        border: 2px solid ${sbBg(theme.mode)};
        display: inline-flex;
        align-items: center;
        justify-content: center;
    `)}
`;

export const OrgSelectorRow = styled.div<{ $isCollapsed?: boolean }>`
    display: flex;
    align-items: center;
    gap: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '8px')};
    padding-right: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '8px')};
    flex-direction: ${({ $isCollapsed }) => ($isCollapsed ? 'column' : 'row')};

    & > *:first-child {
        flex: 1;
        min-width: 0;
    }
`;

export const SettingsIconButton = styled(LocaleNavLink)<{ $isCollapsed?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 28px;
    height: 28px;
    border-radius: 8px;
    background: transparent;
    color: ${({ theme }) => sbIcon(theme.mode)};
    transition: background 0.2s ease, color 0.2s ease;
    cursor: pointer;
    flex-shrink: 0;
    margin-top: ${({ $isCollapsed }) => ($isCollapsed ? '4px' : '0')};
    margin-right: ${({ $isCollapsed }) => ($isCollapsed ? '0' : '7px')};
    margin-bottom: ${({ $isCollapsed }) => ($isCollapsed ? '8px' : '0')};

    svg {
        width: 14px;
        height: 14px;
    }

    &:hover {
        background: ${({ theme }) => sbHoverBg(theme.mode)};
        color: ${({ theme }) => sbTextHover(theme.mode)};
    }

    &.active {
        background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#f3f4f6')};
        color: ${({ theme }) => sbTextHover(theme.mode)};
    }

    ${media.mobile} {
        width: 32px;
        height: 32px;

        svg {
            width: 16px;
            height: 16px;
        }
    }
`;

/* Stopka sidebara — przyklejona do dołu, mieści np. kartę "Staff App". */
export const SidebarFooter = styled.div<{ $isCollapsed?: boolean }>`
    flex-shrink: 0;
    padding: ${({ $isCollapsed }) => ($isCollapsed ? '8px 6px 10px' : '8px 10px 11px')};
    border-top: 1px solid ${({ theme }) => sbDivider(theme.mode)};
    display: flex;
    flex-direction: column;
    gap: 6px;
`;

/* Płaski pasek informacyjny pod kartą "Aplikacja dla zespołu": ikona języka,
   aktualny język panelu i subtelny badge z językiem bazowym (tym, w którym
   user dodaje treści). Bez tła i ramki — ma być cichy, nie klikalny. */
export const LanguageBar = styled.div<{ $isCollapsed?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: ${({ $isCollapsed }) => ($isCollapsed ? 'center' : 'flex-start')};
    gap: 8px;
    padding: ${({ $isCollapsed }) => ($isCollapsed ? '3px 0' : '3px 6px')};
    min-height: 22px;
    color: ${({ theme }) => sbIcon(theme.mode)};

    svg {
        width: 12px;
        height: 12px;
        flex-shrink: 0;
    }
`;

export const LanguageBarText = styled.span`
    font-size: 10px;
    font-weight: 500;
    letter-spacing: 0.02em;
    color: ${({ theme }) => sbText(theme.mode)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
`;

export const LanguageBaseBadge = styled.span`
    margin-left: auto;
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    padding: 0 5px;
    border-radius: 999px;
    font-size: 9px;
    font-weight: 400;
    letter-spacing: 0.03em;
    background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.06)' : '#f3f4f6')};
    border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#dcdfe4')};
    color: ${({ theme }) => (theme.mode === 'dark' ? '#e5e7eb' : '#374151')};
`;

export const ScrollButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    padding: 6px;
    background: ${({ theme }) => sbBg(theme.mode)};
    border: none;
    color: ${({ theme }) => sbIcon(theme.mode)};
    cursor: pointer;
    transition: background 0.2s ease, color 0.2s ease;
    box-shadow: 0 -4px 8px ${({ theme }) => (theme.mode === 'dark' ? 'rgba(0,0,0,0.3)' : 'rgba(15,23,42,0.06)')};

    svg {
        width: 13px;
        height: 13px;
    }

    &:hover {
        background: ${({ theme }) => sbHoverBg(theme.mode)};
        color: ${({ theme }) => sbTextHover(theme.mode)};
    }

    ${media.mobile} {
        padding: 8px;
    }
`;

/* ==========================================================================
   Group flyout — popover panel renderowany do portala (document.body) i
   pozycjonowany absolutnie obok rodzica (right: anchorRect.right + 6).
   ========================================================================== */

export const GroupChevron = styled.span<{ $expanded?: boolean }>`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    margin-left: auto;
    width: 13px;
    height: 13px;
    color: ${({ theme }) => sbIcon(theme.mode)};
    transition: transform 0.2s ease, color 0.15s ease;
    transform: rotate(${({ $expanded }) => ($expanded ? '90deg' : '0deg')});

    svg {
        width: 13px;
        height: 13px;
    }
`;

/* ==========================================================================
   Submenu accordion — subitemy grupy rozwijane inline pod rodzicem (zamiast
   flyout-popovera). Animacja wysokości przez grid-template-rows 0fr -> 1fr.
   ========================================================================== */

export const SubmenuAccordion = styled.div<{ $expanded?: boolean }>`
    display: grid;
    grid-template-rows: ${({ $expanded }) => ($expanded ? '1fr' : '0fr')};
    transition: grid-template-rows 0.22s ease;
`;

export const SubmenuClip = styled.div`
    overflow: hidden;
    min-height: 0;
`;

export const SubmenuInner = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 0;
    padding: 1px 0 2px;
    /* Kreska pionowa pod środkiem ikony rodzica: 9 px padding wiersza + połowa
       19-pikselowego pudełka ikony. */
    margin-left: 18px;
    border-left: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#dcdfe4')};
`;

/* Sub-link bez ikony — sam tekst (+ opcjonalny status badge). Stan aktywny
   sterowany propem $active (nie klasą .active z NavLink), żeby aktywny był
   dokładnie jeden, najbardziej szczegółowy wpis. */
export const SubmenuLink = styled(LocaleNavLink)<{ $disabled?: boolean; $active?: boolean }>`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 7px;
    padding: 3px 10px 3px 15px;
    border-radius: 8px;
    font-size: 12.5px;
    line-height: 1.4;
    font-weight: 500;
    color: ${({ theme }) => sbText(theme.mode)};
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: color 0.15s ease, background 0.15s ease;

    ${({ $disabled }) => $disabled && `
        pointer-events: none;
        opacity: 0.5;
        cursor: not-allowed;
    `}

    &:hover {
        ${({ $disabled, theme }) => !$disabled && `
            color: ${sbTextHover(theme.mode)};
            background: ${sbHoverBg(theme.mode)};
        `}
    }

    ${({ $active, $disabled, theme }) => $active && !$disabled && `
        color: ${sbTextHover(theme.mode)};
        font-weight: 600;
        background: ${theme.mode === 'dark' ? 'rgba(255,255,255,0.10)' : 'rgba(17,24,39,0.06)'};

        &::before {
            content: '';
            position: absolute;
            left: -1px;
            top: 6px;
            bottom: 6px;
            width: 2px;
            border-radius: 0 2px 2px 0;
            background: ${sbTextHover(theme.mode)};
        }
    `}

    > * {
        position: relative;
        z-index: 1;
    }
`;

export const FlyoutPanel = styled.div`
    position: fixed;
    z-index: 200;
    min-width: 240px;
    max-width: 300px;
    max-height: calc(100vh - 16px);
    overflow-y: auto;
    padding: 8px;
    background: ${({ theme }) => sbBg(theme.mode)};
    border: 1px solid ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.08)' : '#dcdfe4')};
    border-radius: 12px;
    box-shadow: 0 12px 28px rgba(15, 23, 42, 0.16), 0 4px 8px rgba(15, 23, 42, 0.08);
    animation: flyoutIn 0.15s ease-out;

    @keyframes flyoutIn {
        from {
            opacity: 0;
            transform: translateX(-4px);
        }
        to {
            opacity: 1;
            transform: translateX(0);
        }
    }

    &::-webkit-scrollbar {
        width: 4px;
    }

    &::-webkit-scrollbar-thumb {
        background: ${({ theme }) => (theme.mode === 'dark' ? 'rgba(255,255,255,0.18)' : 'rgba(15,23,42,0.14)')};
        border-radius: 4px;
    }
`;

export const FlyoutHeader = styled.div`
    padding: 7px 10px 8px;
    margin-bottom: 6px;
    font-size: 10px;
    font-weight: 400;
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: ${({ theme }) => (theme.mode === 'dark' ? '#d1d5db' : '#4b5563')};
    border-bottom: 1px solid ${({ theme }) => sbDivider(theme.mode)};
`;

export const FlyoutSubLink = styled(LocaleNavLink)`
    position: relative;
    display: flex;
    flex-direction: row;
    align-items: center;
    gap: 10px;
    padding: 5px 10px;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    color: ${({ theme }) => sbText(theme.mode)};
    transition: color 0.15s ease, background 0.15s ease;

    svg {
        width: 19px;
        height: 19px;
        color: ${({ theme }) => sbIcon(theme.mode)};
    }

    &:hover {
        color: ${({ theme }) => sbTextHover(theme.mode)};
        background: ${({ theme }) => sbHoverBg(theme.mode)};

        svg { color: ${({ theme }) => sbTextHover(theme.mode)}; }
    }

    ${iconHoverFx}

    &.active {
        color: ${({ theme }) => sbTextHover(theme.mode)};
        background: ${({ theme }) => sbActiveBg(theme.mode)};
        font-weight: 600;

        svg { color: ${({ theme }) => sbTextHover(theme.mode)}; }
    }
`;

export const SidebarFooterRow = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
`;

/* Pinezka w stopce, na prawo od wersji: przypina rozwinięte menu na stałe.
   Widoczna tylko po rozwinięciu. Aktywna = ciemna ikona na szarym tle. */
export const PinButton = styled.button`
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 32px;
    height: 32px;
    flex-shrink: 0;
    padding: 0;
    border: none;
    border-radius: 9999px;
    background: transparent;
    color: ${ds.textSecondary};
    cursor: pointer;
    transition: color 0.15s ease, background-color 0.15s ease;

    &:hover {
        color: ${ds.textPrimary};
        background: ${ds.bgMuted};
    }

    &[data-pinned='true'] {
        color: ${ds.textPrimary};
        background: ${ds.bgMuted};
    }

    svg { width: 16px; height: 16px; stroke-width: 1.6; }
`;

export const SidebarVersion = styled.span`
    display: block;
    padding: 0 12px;
    font-size: 12px;
    line-height: 1.4;
    white-space: nowrap;
    color: ${ds.textMuted};
`;

/* ==========================================================================
   Panel menu jak w hotelspot-audit (AppSidebar.astro, klasy hs-side-*):
   desktop: 72 px (ikony w kołach 40 px), po najechaniu 272 px nad treścią, matowe szkło,
   bez zaokrągleń i cienia, linia po prawej. Wiersze 40 px, rounded-full, odstęp 6 px,
   ikony 18 px, tekst 14 px text-secondary; hover i aktywna pozycja: szare tło (bg-muted).
   Telefon i tablet: zwykła kolumna szuflady.
   ========================================================================== */
export const SidebarPanel = styled.div`
    display: flex;
    flex-direction: column;
    height: 100%;
    min-height: 0;
    background: ${({ theme }) => sbBg(theme.mode)};

    ${media.desktop} {
        position: absolute;
        top: 0;
        left: 0;
        bottom: 0;
        width: 72px;
        /* Odstęp od paska górnego do pierwszej ikony jak w audit (48 px). */
        padding: 48px 15px 24px 16px;
        overflow: hidden;
        /* Linia po prawej (border-default) widoczna zawsze: w wąskim pasku i w rozwiniętym panelu. */
        border-right: 1px solid ${ds.borderDefault};
        transition: width 200ms cubic-bezier(0.2, 0.8, 0.2, 1);

        &[data-open='true'] {
            width: 272px;
            padding-right: 20px;
            /* matowe szkło: treść pod panelem lekko prześwituje i jest rozmyta */
            background: rgba(255, 255, 255, 0.78);
            -webkit-backdrop-filter: blur(14px) saturate(140%);
            backdrop-filter: blur(14px) saturate(140%);
        }

        ${MenuItemsWrapper} {
            gap: 4px;
            padding: 0;
            overflow-x: hidden;
            scrollbar-width: none;

            &::-webkit-scrollbar {
                display: none;
            }
        }

        ${MenuLink}, ${MenuGroupButton} {
            flex-shrink: 0;
            width: 100%;
            height: 40px;
            padding: 0;
            border-radius: 9999px;
            font-size: 14px;
            font-weight: 400;
            line-height: 1.4;
            color: ${ds.textSecondary};
            background: transparent;
            overflow: hidden;

            svg {
                color: currentColor;
            }

            &:hover {
                color: ${ds.textPrimary};
                background: ${ds.bgMuted};

                svg {
                    color: currentColor;
                }
            }
        }

        ${MenuLink}.active, ${MenuGroupButton}[data-active='true'] {
            color: ${ds.textPrimary};
            font-weight: 600;
            background: ${ds.bgMuted};

            svg {
                color: currentColor;
            }
        }

        /* Wyróżnione pozycje (jak „Widoczność w AI" w audycie): tekst z gradientem i kolorowa ikona.
           teal: zieleń, morski, cyjan; gold: pomarańcz, żółć, złoto. */
        ${MenuGroupButton}[data-highlight], ${MenuLink}[data-highlight] {
            ${MenuLinkText} {
                -webkit-background-clip: text;
                background-clip: text;
                color: transparent;
                font-weight: 500;
            }
        }

        ${MenuGroupButton}[data-highlight='teal'], ${MenuLink}[data-highlight='teal'] {
            svg { color: #14b8a6; }

            ${MenuLinkText} { background-image: linear-gradient(90deg, #059669, #14b8a6, #0891b2); }
        }

        ${MenuGroupButton}[data-highlight='gold'], ${MenuLink}[data-highlight='gold'] {
            svg { color: #f59e0b; }

            ${MenuLinkText} { background-image: linear-gradient(90deg, #ea580c, #f59e0b, #ca8a04); }
        }

        ${MenuGroupButton}[data-highlight][data-active='true'] ${MenuLinkText} {
            font-weight: 600;
        }

        ${MenuLinkWrapper} {
            gap: 0;
            justify-content: flex-start;
        }

        ${MenuIconWrapper} {
            width: 40px;
            height: 40px;
            flex-shrink: 0;

            /* Ikony jak w audycie: 17 px i cieńsza kreska (1.6), bez animacji. */
            svg {
                width: 17px;
                height: 17px;
                stroke-width: 1.6;
            }

            /* Znaki marek (Stripe, Oracle) są wypełnione, więc o stopień mniejsze, żeby ważyły tyle co ikony konturowe. */
            svg.brand-icon {
                width: 15px;
                height: 15px;
            }
        }

        ${MenuLinkText} {
            font-size: 14px;
            padding-right: 16px;
            opacity: 0;
            transition: opacity 120ms;
        }

        ${GroupChevron} {
            margin-right: 14px;
            opacity: 0;
            transition: opacity 120ms, transform 0.2s ease;
        }

        .sb-status {
            display: none;
        }

        /* Sekcje: w wąskim pasku sam odstęp, w panelu etykieta (wersaliki 12 px jak w DS). */
        ${SectionLabel} {
            flex-shrink: 0;
            height: 28px;
            padding: 12px 12px 0;
            font-size: 12px;
            font-weight: 500;
            letter-spacing: 0.06em;
            color: ${ds.textMuted};
            white-space: nowrap;
            opacity: 0;
            transition: opacity 120ms;
        }

        ${SubmenuAccordion} {
            display: none;
        }

        ${SubmenuInner} {
            margin-left: 20px;
            padding: 2px 0 4px 8px;
            gap: 2px;
        }

        ${SubmenuLink} {
            height: 34px;
            padding: 0 12px;
            border-radius: 9999px;
            font-size: 14px;
            font-weight: 400;
            color: ${ds.textSecondary};

            &:hover {
                color: ${ds.textPrimary};
                background: ${ds.bgMuted};
            }

            &::before {
                display: none;
            }
        }

        ${SubmenuLink}[data-active='true'] {
            color: ${ds.textPrimary};
            font-weight: 600;
            background: ${ds.bgMuted};
        }

        ${SidebarFooter} {
            border-top: none;
            padding: 12px 0 0;
            opacity: 0;
            transition: opacity 120ms;
        }

        ${PinButton} {
            opacity: 0;
            pointer-events: none;
            transition: opacity 120ms, color 0.15s ease, background-color 0.15s ease;
        }

        &[data-open='true'] ${PinButton} {
            opacity: 1;
            pointer-events: auto;
            transition-delay: 80ms;
        }

        &[data-open='true'] {
            ${MenuLinkText}, ${GroupChevron}, ${SectionLabel}, ${SidebarFooter} {
                opacity: 1;
                transition-delay: 80ms;
            }

            .sb-status {
                display: inline-flex;
            }

            ${SubmenuAccordion} {
                display: grid;
            }
        }
    }

    /* Szuflada na telefonie i tablecie: same nazwy bez ikon, wiersze min. 44 px
       (wygodny cel dotyku), większy tekst i wyraźne wcięcie podmenu. */
    ${media.tabletDown} {
        padding-bottom: env(safe-area-inset-bottom);

        ${MenuItemsWrapper} {
            gap: 2px;
            padding: 4px 12px 12px;
            overscroll-behavior: contain;
        }

        ${MenuIconWrapper} {
            display: none;
        }

        ${MenuLink}, ${MenuGroupButton} {
            flex-shrink: 0;
            min-height: 44px;
            padding: 10px 12px;
            border-radius: 10px;
            font-size: 15px;
            font-weight: 500;
            -webkit-tap-highlight-color: transparent;
        }

        ${MenuLinkWrapper} {
            gap: 8px;
        }

        ${MenuLinkText} {
            font-size: 15px;
            white-space: normal;
        }

        ${GroupChevron} {
            width: 16px;
            height: 16px;

            svg {
                width: 16px;
                height: 16px;
            }
        }

        ${SubmenuInner} {
            margin: 2px 0 6px 12px;
            padding-left: 4px;
            gap: 2px;
        }

        ${SubmenuLink} {
            min-height: 40px;
            padding: 8px 12px;
            font-size: 14px;
            white-space: normal;
            -webkit-tap-highlight-color: transparent;

            &::before {
                top: 10px;
                bottom: 10px;
            }
        }

        ${SidebarFooter} {
            padding: 10px 12px 12px;
        }
    }
`;
